import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {CreateSavedViewResponse, DuplicateSavedViewVariables} from '../components/views/types'
import {LABELS} from '../constants/labels'
import {parseFetchErrorMessage, SAVED_VIEW_QUERY_KEY, SAVED_VIEWS_QUERY_KEY} from '../queries/saved-views'

interface UseDuplicateSavedViewMutationOptions {
  owner: string
  repo: string
}

export function useDuplicateSavedViewMutation({owner, repo}: UseDuplicateSavedViewMutationOptions) {
  const queryClient = useQueryClient()

  return useMutation<CreateSavedViewResponse, Error, DuplicateSavedViewVariables>({
    mutationFn: async ({view, name}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views.json`, {
        method: 'POST',
        body: {
          name,
          query: view.query,
          description: view.description ?? undefined,
          icon: view.icon,
          color: view.color,
          source: 'duplicate',
        },
      })
      if (!response.ok) {
        throw new Error(await parseFetchErrorMessage(response, LABELS.views.duplicateError))
      }
      return (await response.json()) as CreateSavedViewResponse
    },
    onSuccess: ({savedView}) => {
      queryClient.setQueryData([SAVED_VIEW_QUERY_KEY, owner, repo, String(savedView.id)] as const, {savedView})
      void queryClient.invalidateQueries({queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo]})
    },
    onError: () => announce(LABELS.views.duplicateError, {assertive: true}),
  })
}
